/**
 * OrderSheetHistoryList
 * Displays order sheet histories as grouped blocks: order header row, detail rows, then summary row (合計) per order.
 * Uses only existing RA2N atoms (AppTable, AppCard, etc.) and common utils (convGtJDate).
 */

import React, { useMemo } from 'react';
import { AppTable, AppCard } from '@/components/atoms';
import type { ColumnsType } from 'antd/es/table';
import type { OrderSheetHistoryItem } from '@/pages/main/sheetHistories/types';
import { convGtJDate } from '@/utils/dateConverter';

export interface OrderSheetHistoryListProps {
  orders: OrderSheetHistoryItem[];
  loading?: boolean;
  hasSearched?: boolean;
}

type RowType = 'header' | 'detail' | 'summary';

interface FlattenedRow {
  key: string;
  rowType: RowType;
  order_no?: number;
  order_date?: string | null;
  sheet_name?: string;
  unit_amount?: number;
  price?: number;
  quantity?: number;
  subtotal?: number;
  request_delivery_date?: string | null;
  is_include_case?: boolean;
  delivery_fee?: number;
  remarks?: string;
  order_subtotal?: number;
  order_delivery_fee?: number;
}

function formatYen(n: number): string {
  return `${n.toLocaleString()}円`;
}

function getSummaryCellProps(colSpan: number) {
  return (record: FlattenedRow) => (record.rowType === 'summary' ? { colSpan } : {});
}

export const OrderSheetHistoryList: React.FC<OrderSheetHistoryListProps> = ({
  orders,
  loading,
  hasSearched = false,
}) => {
  const { dataSource, columns } = useMemo(() => {
    const rows: FlattenedRow[] = [];
    let rowIndex = 0;

    orders.forEach(order => {
      rows.push({
        key: `h-${order.order_no}-${rowIndex}`,
        rowType: 'header',
        order_no: order.order_no,
        order_date: order.order_date,
      });
      rowIndex += 1;

      (order.details || []).forEach((d, i) => {
        rows.push({
          key: `d-${order.order_no}-${i}`,
          rowType: 'detail',
          sheet_name: d.sheet_name,
          unit_amount: d.unit_amount,
          price: d.price,
          quantity: d.quantity,
          subtotal: d.subtotal,
          request_delivery_date: d.request_delivery_date,
          is_include_case: d.is_include_case,
          delivery_fee: d.delivery_fee,
          remarks: d.remarks,
        });
      });

      rows.push({
        key: `s-${order.order_no}`,
        rowType: 'summary',
        order_subtotal: order.subtotal,
        order_delivery_fee: order.delivery_fee,
      });
    });

    const cols: ColumnsType<FlattenedRow> = [
      {
        title: '注文番号',
        dataIndex: 'order_no',
        key: 'order_no',
        minWidth: 90,
        onCell: getSummaryCellProps(5),
        render: (val: number | undefined, record) => {
          if (record.rowType === 'summary') {
            return <span className="font-medium text-center">合計</span>;
          }
          if (record.rowType === 'header') return val != null ? String(val) : '';
          return '';
        },
      },
      {
        title: '受注日',
        dataIndex: 'order_date',
        key: 'order_date',
        minWidth: 120,
        onCell: getSummaryCellProps(0),
        render: (val: string | null | undefined, record) => {
          if (record.rowType === 'summary') return '';
          if (record.rowType === 'header') return val ? convGtJDate(val) : '';
          return '';
        },
      },
      {
        title: '用紙',
        key: 'sheet_name',
        minWidth: 200,
        onCell: getSummaryCellProps(0),
        render: (_, record) => {
          if (record.rowType === 'summary') return '';
          if (record.rowType === 'detail' && record.sheet_name != null) {
            return `${record.sheet_name} ${record.unit_amount ?? 0}枚`;
          }
          return '';
        },
      },
      {
        title: '単価(税込)',
        dataIndex: 'price',
        key: 'price',
        minWidth: 90,
        align: 'right',
        onCell: getSummaryCellProps(0),
        render: (val: number | undefined, record) => {
          if (record.rowType === 'summary') return '';
          return record.rowType === 'detail' && val != null ? formatYen(val) : '';
        },
      },
      {
        title: '数量',
        dataIndex: 'quantity',
        key: 'quantity',
        minWidth: 70,
        align: 'right',
        onCell: getSummaryCellProps(0),
        render: (val: number | undefined, record) => {
          if (record.rowType === 'summary') return '';
          return record.rowType === 'detail' && val != null ? String(val) : '';
        },
      },
      {
        title: '金額',
        dataIndex: 'subtotal',
        key: 'subtotal',
        minWidth: 100,
        align: 'right',
        render: (val: number | undefined, record) =>
          record.rowType === 'detail' && val != null
            ? formatYen(val)
            : record.rowType === 'summary' && record.order_subtotal != null
              ? formatYen(record.order_subtotal)
              : '',
      },
      {
        title: '納品希望日',
        dataIndex: 'request_delivery_date',
        key: 'request_delivery_date',
        minWidth: 120,
        onCell: getSummaryCellProps(2),
        render: (val: string | null | undefined, record) => {
          if (record.rowType === 'summary') return '';
          return record.rowType === 'detail' ? (val ? convGtJDate(val) : '') : '';
        },
      },
      {
        title: 'ケース',
        dataIndex: 'is_include_case',
        key: 'is_include_case',
        minWidth: 70,
        align: 'center',
        onCell: getSummaryCellProps(0),
        render: (val: boolean | undefined, record) => {
          if (record.rowType === 'summary') return '';
          return record.rowType === 'detail' && val ? '○' : '';
        },
      },
      {
        title: '送料',
        dataIndex: 'delivery_fee',
        key: 'delivery_fee',
        minWidth: 90,
        align: 'right',
        render: (val: number | undefined, record) =>
          record.rowType === 'detail' && val != null
            ? formatYen(val)
            : record.rowType === 'summary' && record.order_delivery_fee != null
              ? formatYen(record.order_delivery_fee)
              : '',
      },
      {
        title: '備考',
        dataIndex: 'remarks',
        key: 'remarks',
        minWidth: 120,
        render: (val: string | undefined, record) => {
          if (record.rowType === 'summary') return '';
          return record.rowType === 'detail' ? (val ?? '') : '';
        },
      },
    ];

    return { dataSource: rows, columns: cols };
  }, [orders]);

  const rowClassName = (record: FlattenedRow) => {
    if (record.rowType === 'summary') {
      return 'order-sheet-summary-row [&>td]:!bg-blue-50 font-medium border-b-2 border-blue-200';
    }
    return 'bg-white';
  };

  return (
    <AppCard>
      <AppTable<FlattenedRow>
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={false}
        hasFilters={hasSearched}
        rowClassName={rowClassName}
        scroll={{ x: 'max-content' }}
      />
    </AppCard>
  );
};

export default OrderSheetHistoryList;
